import { useNavigate, useRouter } from "@tanstack/react-router";

import { useMemo } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { gameUrl } from "@/helpers/games";
import useHistory from "@/hooks/use-history";
import { useIsBreakPoint } from "@/hooks/use-is-break-point";

import GameDetails from "./game-details";
import { BackIcon, CloseIcon } from "./icons";
import OptionsMenu from "./options-menu";
import Review from "./review";
import type { GameType, ReviewType } from "./types";

interface ReviewModalProps {
  backUrl?: string;
  game?: GameType;
  review: ReviewType;
}

export default function ReviewModal({
  backUrl,
  game: gameProp,
  review,
}: ReviewModalProps) {
  const { canGoBack } = useHistory();
  const navigate = useNavigate();
  const router = useRouter();
  const isDesktop = useIsBreakPoint("md");

  const game = useMemo(
    () => gameProp || (review.escape.game as GameType),
    [gameProp, review]
  );

  const handleClose = () => {
    if (canGoBack) {
      router.history.back();
    } else {
      navigate({ to: backUrl || gameUrl(game) });
    }
  };

  return (
    <>
      <meta content="noindex" name="robots" />
      <Dialog onOpenChange={(open) => !open && handleClose()} open>
        <DialogContent
          className="flex min-h-full w-full flex-col gap-0 rounded-none bg-[url(/images/main-bg.jpg)] bg-cover bg-top bg-no-repeat md:min-h-auto md:w-3xl md:max-w-none md:rounded-[15px]"
          overlayClassName="bg-gray-cool/64 items-start"
        >
          <DialogHeader className="relative h-16 flex-row items-center justify-center border-navy-gray md:border-b">
            <Button
              aria-label="Close"
              className="absolute top-0 left-0 flex h-full px-6"
              onClick={handleClose}
            >
              {isDesktop ? <CloseIcon /> : <BackIcon />}
            </Button>
            <DialogTitle className="font-semibold text-16 text-white md:text-15">
              {isDesktop ? `${review.user.username}’s review` : "Review"}
            </DialogTitle>
            <OptionsMenu
              className="absolute top-0 right-0 h-full w-16"
              game={game}
              review={review}
            />
          </DialogHeader>
          <div className="px-5 md:p-6">
            <GameDetails game={game} />
            <Review game={game} isFullReview review={review} />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
